import { atom, computed } from 'nanostores';
import { toast } from 'react-toastify';
import { z } from 'zod';
import {
  batchDeleteMembers,
  batchResendInvitations,
  batchUpdateMembers,
  updateMember,
} from '~/lib/.client/api/organizations';
import { PLAN_LEVELS, type PlanLevel } from '~/lib/.client/plans';
import {
  trackTeamMemberInvitationResent,
  trackTeamMemberRoleChanged,
  trackTeamMemberUpgraded,
  trackTeamMemberDowngraded,
  trackTeamMemberRemoved,
} from '~/lib/analytics/team-membership';
import { userStore } from '~/lib/stores/user';
import { type OrganizationData, type OrganizationMembership, type OrganizationUserRoles } from '~/types/organization';
import { SUBSCRIPTION_INTERVALS, type SubscriptionInterval } from '~/types/subscription';
import type { UserData } from '~/types/user';
import { hmrPropertyFactory } from '~/utils/hmr';

interface CreateTeamSlugData {
  level: PlanLevel;
  interval: SubscriptionInterval;
}

export interface OrganizationInvitee {
  id: number;
  user: Pick<UserData, 'avatar' | 'email' | 'username' | 'name' | 'providerId'>;
  role: OrganizationUserRoles;
}

interface CreateSubscriptionData {
  organization: OrganizationData;
  level: PlanLevel;
  interval: SubscriptionInterval;
  members: OrganizationMembership[];
}

export type UpdateTeamDataType = 'upgrade' | 'downgrade' | 'remove' | 'change_role';

export interface UpdateSubscriptionData {
  type: UpdateTeamDataType;
  members: OrganizationMembership[];
  organization: OrganizationData;
  newPlan?: PlanLevel;
  newRole?: OrganizationUserRoles;
}

const PLAN_LEVELS_SCHEMA = z.enum(PLAN_LEVELS);
const SUBSCRIPTION_INTERVALS_SCHEMA = z.enum(SUBSCRIPTION_INTERVALS);

const hmr = hmrPropertyFactory('TeamStore');

export class TeamStore {
  @hmr()
  members = atom<OrganizationMembership[]>([]);

  @hmr()
  isLoadingTeam = atom(false);

  @hmr()
  createTeamSlugData = atom<CreateTeamSlugData | null>(null);

  @hmr()
  createTeamData = atom<CreateSubscriptionData | null>(null);

  @hmr()
  updateTeamData = atom<UpdateSubscriptionData | null>(null);

  @hmr()
  selectedMembers = atom<string[]>([]);

  isAllSelected = computed([this.members, this.selectedMembers], (members, selected) => {
    const ids = members.map((member) => member.user.username);
    return selected.length === ids.length && ids.length > 0;
  });

  selectAllMembers = () => {
    const isAllSelected = this.isAllSelected.get();
    const members = this.members.get();
    const ids = members.map((member) => member.user.username);
    this.selectedMembers.set(isAllSelected ? [] : ids);
  };

  selectMember = (username: string) => {
    const selected = this.selectedMembers.get();
    this.selectedMembers.set(
      selected.includes(username) ? selected.filter((i) => i !== username) : [...selected, username],
    );
  };

  resendInvites = (usernames: string[], organization: OrganizationData) => {
    const oldMembers = [...this.members.get()];
    const currentUser = userStore.getUser();

    const updatedMembers = oldMembers.filter(
      (member) => usernames.includes(member.user.username) && !member.user.cannotSendInvite,
    );

    if (updatedMembers.length > 0) {
      batchResendInvitations({
        organization,
        members: updatedMembers.map((m) => ({ id: m.id })),
      })
        .then(() => {
          teamStore.selectedMembers.set([]);
          toast.success(
            updatedMembers.length > 1 ? 'The invitations have been resent' : 'The invitation has been resent',
          );

          // track successful invitation resent
          trackTeamMemberInvitationResent(organization, updatedMembers, currentUser, oldMembers);
        })
        .catch((error) => {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }
        });
    } else {
      toast.warning(`You can't resend invites to any of the selected users`);
    }
  };

  changeRoles = async (
    usernames: string[],
    organization: OrganizationData,
    option: { isSingleUpdate?: boolean; newRole: OrganizationUserRoles },
  ) => {
    const oldMembers = [...this.members.get()];
    const createTeamData = this.createTeamData.get();
    const currentUser = userStore.getUser();

    const membersToUpdate = oldMembers.filter(
      (member) => usernames.includes(member.user.username) && member.role !== option.newRole,
    );

    if (membersToUpdate.length === 0) {
      toast.warning(`All selected users already have the ${option.newRole.replaceAll('_', ' ')} role`);
      return;
    }

    // track role changes
    const roleChanges = membersToUpdate.map((member) => ({
      member,
      oldRole: member.role,
      newRole: option.newRole,
    }));

    if (!!createTeamData) {
      teamStore.createTeamData.set({
        ...createTeamData,
        members: oldMembers.map((member) => ({
          ...member,
          role: usernames.includes(member.user.username) ? option.newRole : member.role,
        })),
      });
      teamStore.selectedMembers.set([]);
      batchUpdateMembers({ organization, members: membersToUpdate })
        .then(() => {
          // track successful role changes
          trackTeamMemberRoleChanged(organization, roleChanges, currentUser, oldMembers);
        })
        .catch((error) => {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }

          teamStore.createTeamData.set({
            ...createTeamData,
            members: oldMembers,
          });
        });
    } else {
      if (option.isSingleUpdate) {
        try {
          await updateMember({ organization, member: { ...membersToUpdate[0], role: option.newRole } });

          // track successful single role change
          trackTeamMemberRoleChanged(organization, roleChanges, currentUser, oldMembers);
        } catch (error) {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }
        }
      } else {
        teamStore.updateTeamData.set({
          organization,
          newRole: option.newRole,
          type: 'change_role',
          members: membersToUpdate,
        });

        // note: tracking for batch updates will happen in UpdateTeamScreen when confirmed
      }
    }
  };

  updateMembersSubscription = (usernames: string[], organization: OrganizationData, isUpgrade: boolean) => {
    const oldMembers = [...this.members.get()];
    const createTeamData = this.createTeamData.get();

    const membersToUpdate = oldMembers.filter(
      (m) => usernames.includes(m.user.username) && m.withMembership !== isUpgrade,
    );

    if (membersToUpdate.length > 0) {
      if (!!createTeamData) {
        const newMembers = oldMembers.map((member) => ({
          ...member,
          withMembership:
            usernames.includes(member.user.username) && member.withMembership !== isUpgrade
              ? isUpgrade
              : member.withMembership,
        }));
        teamStore.createTeamData.set({
          ...createTeamData,
          members: newMembers,
        });
        teamStore.selectedMembers.set([]);

        // track subscription changes for team creation flow
        const currentUser = userStore.getUser();

        if (isUpgrade) {
          trackTeamMemberUpgraded(organization, membersToUpdate, currentUser, oldMembers);
        } else {
          trackTeamMemberDowngraded(organization, membersToUpdate, currentUser, oldMembers);
        }
      } else {
        teamStore.updateTeamData.set({
          organization,
          type: isUpgrade ? 'upgrade' : 'downgrade',
          members: membersToUpdate,
        });

        // note: tracking for team updates will happen in UpdateTeamScreen when confirmed
      }
    } else {
      toast.warning(`There are no users to ${isUpgrade ? 'upgrade' : 'downgrade'}`);
    }
  };

  deleteMembers = (usernames: string[], organization: OrganizationData) => {
    const oldMembers = [...this.members.get()];
    const membersToDelete = oldMembers.filter((member) => usernames.includes(member.user.username));
    const createTeamData = this.createTeamData.get();
    const currentUser = userStore.getUser();

    if (!!createTeamData) {
      teamStore.createTeamData.set({
        ...createTeamData,
        members: oldMembers.filter((member) => !usernames.includes(member.user.username)),
      });
      teamStore.selectedMembers.set([]);
      batchDeleteMembers({
        organization,
        members: membersToDelete.map((m) => ({ id: m.id })),
      })
        .then(() => {
          // track successful member removal for team creation flow
          trackTeamMemberRemoved(organization, membersToDelete, currentUser, oldMembers);
        })
        .catch((error) => {
          if (error instanceof Error && error.message) {
            toast.error(error.message);
          }

          this.createTeamData.set({
            ...createTeamData,
            members: oldMembers,
          });
        });
    } else {
      this.updateTeamData.set({
        organization,
        type: 'remove',
        members: membersToDelete,
      });

      // note: tracking for team updates will happen in UpdateTeamScreen when confirmed
    }
  };

  openTeamInviteModal = (team: OrganizationData, searchParams: URLSearchParams) => {
    const levelRaw = searchParams.get('level');
    const intervalRaw = searchParams.get('interval');
    const level = PLAN_LEVELS_SCHEMA.safeParse(levelRaw).success ? (levelRaw as PlanLevel) : 'teams_1';

    const interval = SUBSCRIPTION_INTERVALS_SCHEMA.safeParse(intervalRaw).success
      ? (intervalRaw as SubscriptionInterval)
      : 'monthly';

    if (team && level && interval) {
      teamStore.createTeamData.set({
        organization: team,
        level,
        interval,
        members: [],
      });
    }
  };
}

export const teamStore = new TeamStore();

if (import.meta.env.DEV) {
  Object.assign(globalThis, { TeamStore });
}
